import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Helmet } from 'react-helmet';
import config from 'react-global-configuration';

import {
  Button,
  Container,
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  CoreLink,
  UI_OPERATION_STATUS_REQUEST,
  userGetCurrent,
} from '@uncinc/react-drupal-core';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';

import useCurrentUser from '../../../hooks/useCurrentUser';
import useTypedDispatch from '../../../hooks/useTypedDispatch';

import { createOrUpdateUserProfile } from '../../../store/actions/Profile';
import {
  APP_OPTIONS,
  DEFAULT_LANGUAGE,
  OTHER_FORM_ITEM_ID,
} from '../../../config/constants';
import { ROUTE_CUSTOMER_SERVICE } from '../../../config/routes';
import {
  ProfileForm,
  getProfileValidationSchema,
} from '../../../components/ProfileForm';

import checkConstraints from '../../../helpers/ssoServices';


// Move to different file?
const performRedirect = (urlParams: URLSearchParams) => {
  let redirectUrl = urlParams.get('destination') || urlParams.get('redirect_url');
  if (redirectUrl) {
    // For OAuth paths, use full backend URL to avoid React Router handling
    if (redirectUrl.startsWith('/oauth')) {
      const API_URL = config.get('API_URL');
      redirectUrl = `${API_URL}${redirectUrl}${window.location.search}`;
    } else {
      redirectUrl = `${redirectUrl}${window.location.search}`;
    }
  }

  window.location.replace(redirectUrl || '/');
};

/**
 * SSO signin profile validation and update page component.
 *
 * Uses the constraints set in the config to present a user with a profile edit page limited to
 *  the fields that do not met the set constraints.
 */
const SsoInfo = () => {
  const PAGE_TITLE = 'Extra info | VeiligheidNL';
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const dispatch = useTypedDispatch();
  const formRef = useRef(null);
  const profileValidationSchema = getProfileValidationSchema();

  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));

  useEffect(() => {
    dispatch(userGetCurrent());
  }, []);


  const onSubmit = (values: any) => {
    // Update the profile.
    const profileFormData = Object.fromEntries(
      Object.entries(values).filter(([key]) => !key.endsWith('_other')),
    );

    if (profileFormData.field_work_area === OTHER_FORM_ITEM_ID) {
      profileFormData.field_work_area = values.field_work_area_other;
    }
    if (profileFormData.field_job_title === OTHER_FORM_ITEM_ID) {
      profileFormData.field_job_title = values.field_job_title_other;
    }

    dispatch(createOrUpdateUserProfile(profileFormData) as any);
  };

  const clientId = urlParams.get('client_id') || '';
  if (!clientId) {
    // Something is wrong, use default redirect
    performRedirect(urlParams);
  }

  const service = APP_OPTIONS.ssoServices.find((s) => s.clientIds.includes(clientId));
  const user = useCurrentUser();
  const failedConstraints = checkConstraints(user, service?.constraints);

  let fieldsToShow: string[] = [];
  let initialValues = null;
  let validationSchema = null;
  if (failedConstraints.pending === false) {
    const fieldsWithUnmetConstraint = Object.keys(failedConstraints.unmetConstraints);

    initialValues = Object.fromEntries(fieldsWithUnmetConstraint.map((key) => [key, '']));
    fieldsToShow = fieldsWithUnmetConstraint;

    validationSchema = Yup.object({
      ...Object.fromEntries(
        Object.entries(profileValidationSchema).filter(([key]) => fieldsToShow.includes(key)),
      ),
    });
  }

  useEffect(() => {
    if (
      failedConstraints.pending === false
      && Object.keys(failedConstraints.unmetConstraints).length === 0
    ) {
      // All constraints met
      performRedirect(urlParams);
    }
  }, [failedConstraints, urlParams]);

  const constraintsMet = failedConstraints.pending === false
    && Object.keys(failedConstraints.unmetConstraints).length === 0;

  return !constraintsMet && (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <Container small>
        <div className="FormPage PageLogin PageMinimal" id="main">
          <div className="PageMinimal__top">
            <CoreLink className="Logo" href="/">
              <img src={LogoImageVNL} alt="VeiligheidNL" />
            </CoreLink>
            <h1>{t('sso.additional_info.valanalyse_title')}</h1>
          </div>
          <div className="PageMinimal__content">
            <FormikWrapper
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              innerRef={formRef}
              initialValues={initialValues}
            >
              <ProfileForm
                formRef={formRef}
                fieldsToShow={fieldsToShow}
                allowedJobTitleOptions={
                  service?.constraints?.field_job_title?.allow
                }
                allowedWorkFieldOptions={
                  service?.constraints?.field_work_area?.allow
                }
              />
              <div className="Form__actions">
                <Button
                  type="submit"
                  className={`Button Button--primary ${(profileUpdateStatus === UI_OPERATION_STATUS_REQUEST) ? 'Button--loading' : ''}`}
                  onClick={() => {
                    // Temp fix for touching the select fields to show error.
                    // https://uncinc.monday.com/boards/1674548856/pulses/9431098326
                    // @ts-expect-error
                    formRef?.current?.setFieldTouched('field_work_area');
                    // @ts-expect-error
                    formRef?.current?.setFieldTouched('field_job_title');
                  }}
                >
                  {t('general.save')}
                </Button>
              </div>
            </FormikWrapper>
          </div>
          <div className="PageMinimal__usp">
            <div className="PageMinimal__usp__link">
              <CoreLink href={ROUTE_CUSTOMER_SERVICE} target="_blank" className="Button Button--secondary Button--help">
                {t('general.need_help')}
              </CoreLink>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default SsoInfo;
